import { 
  collection, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  query, 
  orderBy, 
  where, 
  Timestamp 
} from 'firebase/firestore';
import { db } from '../firebase';
import { EventItem, NewEvent } from '../types';

const isDev = Boolean((import.meta as any).env?.DEV);
const logDebug = (...args: any[]) => {
  if (isDev) console.log(...args);
};

// Funzione per convertire un documento Firestore in EventItem
function fromFirestore(doc: any): EventItem {
  const data = doc.data();
  logDebug('🔍 [fromFirestore] Convertendo documento:', doc.id, data);
  
  // Gestione più robusta delle date
  let startAt = new Date();
  let endAt = undefined;
  let createdAt = new Date();
  let updatedAt = new Date();
  
  try {
    if (data.startAt) {
      startAt = data.startAt.toDate ? data.startAt.toDate() : new Date(data.startAt);
    }
  } catch (error) {
    console.warn('⚠️ [fromFirestore] Errore conversione startAt:', error);
    startAt = new Date();
  }
  
  try {
    if (data.endAt) {
      endAt = data.endAt.toDate ? data.endAt.toDate() : new Date(data.endAt);
    }
  } catch (error) {
    console.warn('⚠️ [fromFirestore] Errore conversione endAt:', error);
  }
  
  try {
    if (data.createdAt) {
      createdAt = data.createdAt.toDate ? data.createdAt.toDate() : new Date(data.createdAt);
    }
  } catch (error) {
    console.warn('⚠️ [fromFirestore] Errore conversione createdAt:', error);
  }
  
  try {
    if (data.updatedAt) {
      updatedAt = data.updatedAt.toDate ? data.updatedAt.toDate() : new Date(data.updatedAt);
    }
  } catch (error) {
    console.warn('⚠️ [fromFirestore] Errore conversione updatedAt:', error);
  }
  
  return {
    id: doc.id,
    title: data.title || '',
    description: data.description || '',
    location: data.location || '',
    imageUrl: data.imageUrl || '',
    startAt,
    endAt,
    allDay: data.allDay || false,
    category: data.category || '',
    tags: data.tags || [],
    price: data.price || '',
    organizer: data.organizer || '',
    contactInfo: data.contactInfo || '',
    website: data.website || '',
    coordinates: data.coordinates || undefined,
    createdAt,
    updatedAt,
  };
}

// Funzione per verificare se un evento è in corso o futuro
function isUpcomingOrOngoing(event: EventItem, now: Date): boolean {
  // Eventi futuri
  if (event.startAt >= now) return true;
  
  // Eventi in corso (con endAt)
  if (event.endAt && event.endAt > now) return true;
  
  // Eventi all-day di oggi
  if (event.allDay) {
    const today = new Date();
    const eventDate = new Date(event.startAt);
    return (
      eventDate.getFullYear() === today.getFullYear() &&
      eventDate.getMonth() === today.getMonth() &&
      eventDate.getDate() === today.getDate()
    );
  }
  
  return false;
}

// Recupera tutti gli eventi futuri e in corso
export async function fetchUpcomingEvents(): Promise<EventItem[]> {
  try {
    const now = new Date();
    const eventsCol = collection(db, 'events');
    
    // Query per eventi futuri
    const qFuture = query(
      eventsCol, 
      where('startAt', '>=', Timestamp.fromDate(now)), 
      orderBy('startAt', 'asc')
    );
    
    // Query per eventi in corso
    const qOngoing = query(
      eventsCol, 
      where('endAt', '>', Timestamp.fromDate(now))
    );
    
    const [snapFuture, snapOngoing] = await Promise.all([
      getDocs(qFuture), 
      getDocs(qOngoing)
    ]);

    const byId = new Map<string, EventItem>();
    
    // Aggiungi eventi futuri
    snapFuture.docs.map(fromFirestore).forEach((ev) => byId.set(ev.id, ev));
    
    // Aggiungi eventi in corso
    snapOngoing.docs.map(fromFirestore).forEach((ev) => byId.set(ev.id, ev));

    // Filtra e ordina
    const combined = Array.from(byId.values())
      .filter((ev) => isUpcomingOrOngoing(ev, now))
      .sort((a, b) => a.startAt.getTime() - b.startAt.getTime());

    return combined;
  } catch (error) {
    console.error('Errore nel recupero degli eventi:', error);
    return [];
  }
}

// Recupera tutti gli eventi
export async function fetchAllEvents(): Promise<EventItem[]> {
  try {
    logDebug('🔄 [fetchAllEvents] Inizio recupero eventi...');
    const eventsCol = collection(db, 'events');
    
    // Usa direttamente getDocs semplice per evitare problemi con orderBy
    logDebug('🔄 [fetchAllEvents] Uso getDocs semplice per evitare problemi con orderBy');
    const snapshot = await getDocs(eventsCol);
    logDebug('✅ [fetchAllEvents] getDocs semplice riuscito');
    
    logDebug('📊 [fetchAllEvents] Documenti trovati:', snapshot.size);
    const events = snapshot.docs.map(fromFirestore);
    
    // Ordina manualmente se non è stato fatto dalla query
    events.sort((a, b) => a.startAt.getTime() - b.startAt.getTime());
    
    logDebug('✅ [fetchAllEvents] Eventi convertiti e ordinati:', events.length);
    
    return events;
  } catch (error) {
    console.error('❌ [fetchAllEvents] Errore nel recupero di tutti gli eventi:', error);
    return [];
  }
}

// Crea un nuovo evento
export async function createEvent(eventData: NewEvent): Promise<string> {
  try {
    const eventsCol = collection(db, 'events');
    const docRef = await addDoc(eventsCol, {
      ...eventData,
      startAt: Timestamp.fromDate(new Date(eventData.startAt)),
      endAt: eventData.endAt ? Timestamp.fromDate(new Date(eventData.endAt)) : null,
      createdAt: Timestamp.fromDate(new Date()),
      updatedAt: Timestamp.fromDate(new Date()),
    });
    
    logDebug('Evento creato con ID:', docRef.id);
    return docRef.id;
  } catch (error) {
    console.error('Errore nella creazione dell\'evento:', error);
    throw error;
  }
}

// Aggiorna un evento esistente
export async function updateEvent(eventId: string, eventData: Partial<NewEvent>): Promise<void> {
  try {
    const eventRef = doc(db, 'events', eventId);
    const updateData: any = {
      ...eventData,
      updatedAt: Timestamp.fromDate(new Date()),
    };
    
    // Converti le date se presenti
    if (eventData.startAt) {
      updateData.startAt = Timestamp.fromDate(new Date(eventData.startAt));
    }
    if (eventData.endAt) {
      updateData.endAt = Timestamp.fromDate(new Date(eventData.endAt));
    }
    
    await updateDoc(eventRef, updateData);
    logDebug('Evento aggiornato:', eventId);
  } catch (error) {
    console.error('Errore nell\'aggiornamento dell\'evento:', error);
    throw error;
  }
}

// Elimina un evento
export async function deleteEvent(eventId: string): Promise<void> {
  try {
    const eventRef = doc(db, 'events', eventId);
    await deleteDoc(eventRef);
    logDebug('Evento eliminato:', eventId);
  } catch (error) {
    console.error('Errore nell\'eliminazione dell\'evento:', error);
    throw error;
  }
}
